import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminUserList from '../components/dashboard/AdminManagement/AdminUserList';
import AdminUserForm from '../components/dashboard/AdminManagement/AdminUserForm';
import AdminUserDetails from '../components/dashboard/AdminManagement/AdminUserDetails';
import { UserGroupIcon } from '@heroicons/react/24/outline';

const AdminManagementPage = () => {
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState('list'); // 'list', 'form', 'details'
  const [selectedAdminUser, setSelectedAdminUser] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  
  useEffect(() => {
    const userRole = localStorage.getItem('role');
    if (userRole !== 'SUPER_ADMIN') {
      // Redirect to dashboard if user is not SUPER_ADMIN
      navigate('/dashboard');
      return;
    }
  }, [navigate]);

  // Check role again for rendering
  const userRole = localStorage.getItem('role');
  if (userRole !== 'SUPER_ADMIN') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg text-center max-w-md">
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">
            You don't have permission to access this page. Only Super Administrators can manage admin accounts.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const handleEditUser = (adminUser) => {
    setSelectedAdminUser(adminUser);
    setCurrentView('form');
  };

  const handleViewDetails = (adminUserId) => {
    setSelectedAdminUser({ id: adminUserId });
    setCurrentView('details');
  };

  const handleSaveSuccess = () => {
    setCurrentView('list');
    setSelectedAdminUser(null);
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedAdminUser(null);
  };

  const handleBackToDetails = (adminUser) => {
    setSelectedAdminUser(adminUser);
    setCurrentView('details');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Success Message */}
      {showSuccessMessage && (
        <div className="fixed top-4 right-4 z-50">
          <div className="bg-green-500 text-white px-4 py-2 rounded-md shadow-lg flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
            Admin user saved successfully!
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Main Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {currentView === 'list' && (
            <AdminUserList 
              onEditUser={handleEditUser}
              onViewDetails={handleViewDetails}
            />
          )}
          
          {currentView === 'form' && (
            <AdminUserForm
              adminUser={selectedAdminUser}
              onSave={handleSaveSuccess}
              onCancel={handleBackToList}
            />
          )}
          
          {currentView === 'details' && (
            <AdminUserDetails
              adminUserId={selectedAdminUser?.id}
              onBack={handleBackToList}
              onEdit={handleBackToDetails}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminManagementPage;